import { useEffect } from "react";
import { accountMessage } from "../account/messages";
import { PromoCode } from "./PromoCode";
import { SheetOverlay } from "./SheetOverlay";
import type { ShopActions, ShopView } from "./view-model";
import { Num } from "./Num";
import { PixelIcon } from "./PixelIcon";

export interface ShopSheetProps {
  shop?: ShopView;
  shopActions?: ShopActions;
  onOpenAccount(): void;
  onClose(): void;
}

export function ShopSheet({
  shop,
  shopActions,
  onOpenAccount,
  onClose,
}: ShopSheetProps) {
  useEffect(() => {
    void shopActions?.onLoadShop();
  }, [shopActions]);

  return (
    <SheetOverlay
      testId="shop-sheet"
      title="Shop"
      onClose={onClose}
    >
      <div className="sb">
        {!shop ? (
          <div className="note" data-testid="shop-unavailable">
            The shop is not available right now.
          </div>
        ) : (
          <>
            <div className="note" data-testid="shop-promise">
              Every item shows its exact price and exactly what you get. Nothing is random, and nothing is a timed offer.
            </div>

            {shop.testMode ? (
              <div className="note" data-testid="shop-test-note">
                Payments are in test mode. No real money is used.
              </div>
            ) : null}

            {shop.result ? (
              <div
                className="row col"
                data-testid="shop-result"
                style={{ gap: "6px", alignItems: "flex-start" }}
              >
                {shop.result === "done" && shop.confirming ? (
                  <div role="status">Checking your purchase…</div>
                ) : shop.result === "done" && !shop.confirming ? (
                  <div role="status">Thank you! Your item is yours now.</div>
                ) : shop.result === "cancelled" ? (
                  <div>No payment was made.</div>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  data-testid="shop-result-dismiss"
                  onClick={() => shopActions?.onDismissShopResult()}
                >
                  OK
                </button>
              </div>
            ) : null}

            {shop.problem ? (
              <div className="note" data-testid="shop-problem" role="alert">
                {accountMessage(shop.problem)}
              </div>
            ) : null}

            {!shop.signedIn ? (
              <div className="row" style={{ justifyContent: "space-between" }}>
                <span className="note">Sign in to buy things.</span>
                <button
                  type="button"
                  className="btn"
                  data-testid="shop-signin"
                  onClick={onOpenAccount}
                >
                  Open Account
                </button>
              </div>
            ) : null}

            {shop.signedIn && !shop.canBuy && shop.loaded ? (
              <div
                className="row col"
                data-testid="shop-blocked"
                style={{ gap: "6px", alignItems: "flex-start" }}
              >
                <div style={{ fontWeight: 700 }}>To buy things you still need:</div>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {shop.missing.map((req) => (
                    <li key={req}>
                      {req === "google" ? "Connect to Google" : req === "parent" ? "A parent's OK" : req}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn"
                  data-testid="shop-open-account"
                  onClick={onOpenAccount}
                >
                  Open Account
                </button>
              </div>
            ) : null}

            {shop.loaded && shop.items.length === 0 ? (
              <div className="note" data-testid="shop-empty">
                Nothing is for sale right now.
              </div>
            ) : null}

            <div
              className="shop-list"
              data-testid="shop-list"
              style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "8px" }}
            >
              {shop.items.map((item) => (
                <div
                  key={item.id}
                  className="row col shop-item"
                  data-testid="shop-item"
                  data-item-id={item.id}
                  data-owned={item.owned ? "true" : "false"}
                  style={{ alignItems: "flex-start", gap: "6px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      width: "100%",
                      gap: "8px",
                    }}
                  >
                    <h4 style={{ margin: 0 }}>{item.name}</h4>
                    <Num>{item.priceLabel}</Num>
                  </div>
                  <div className="note" style={{ width: "100%" }}>
                    {item.description}
                  </div>
                  {item.owned ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        width: "100%",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span data-testid="shop-owned" style={{ color: "#4fd1c5", fontWeight: 700 }}>
                        <PixelIcon name="check" /> Owned
                      </span>
                    </div>
                  ) : shop.signedIn ? (
                    <div style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="btn"
                        data-testid="shop-buy"
                        aria-label={`Buy ${item.name} for ${item.priceLabel}`}
                        disabled={shop.busy || !shop.canBuy}
                        onClick={() => void shopActions?.onBuy(item.id)}
                      >
                        {`Buy for ${item.priceLabel}`}
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <PromoCode shop={shop} shopActions={shopActions} />
          </>
        )}
      </div>
    </SheetOverlay>
  );
}
